import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import {
  getSidebarDefaultPx,
  getSidebarMinPx,
  SIDEBAR_MAX_PX,
} from 'src/constants/uiConsts';
import { useFeatureFlags } from 'src/context';
import { RouteContext } from 'src/context/routeContext';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';

// Must match the resizable group and panel ids AppShell renders the main sidebar with.
const MAIN_SIDEBAR_GROUP_ID = 'primary-sidebar';
const MAIN_SIDEBAR_PANEL_ID = 'primary-sidebar-panel';
const MAIN_CONTENT_PANEL_ID = 'primary-content-panel';
const COMPANY_SWITCHER_PANEL_ID = 'company-switcher-panel';
const PERSISTED_LAYOUT_KEY_PREFIX = 'react-resizable-panels';

/**
 * The main sidebar's width as its resizable group last saved it: a percentage
 * of the window, which the group re-saves whenever its layout changes.
 */
const readMainSidebarPercent = (
  showCompanySwitcher: boolean,
): number | undefined => {
  const panelIds = showCompanySwitcher
    ? [COMPANY_SWITCHER_PANEL_ID, MAIN_SIDEBAR_PANEL_ID, MAIN_CONTENT_PANEL_ID]
    : [MAIN_SIDEBAR_PANEL_ID, MAIN_CONTENT_PANEL_ID];
  try {
    const stored = localStorage.getItem(
      [PERSISTED_LAYOUT_KEY_PREFIX, MAIN_SIDEBAR_GROUP_ID, ...panelIds].join(
        ':',
      ),
    );
    if (!stored) return undefined;
    const layout: unknown = JSON.parse(stored);
    if (
      typeof layout !== 'object' ||
      layout === null ||
      !(MAIN_SIDEBAR_PANEL_ID in layout)
    ) {
      return undefined;
    }
    const percent = layout[MAIN_SIDEBAR_PANEL_ID];
    return typeof percent === 'number' ? percent : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Under `new-settings`, the fixed settings drawer takes the main sidebar's
 * width, so the rail doesn't move between the app and Settings. Null
 * everywhere else, where callers keep their own width.
 */
export const useSettingsSidebarWidth = (): number | null => {
  const { EnableNewSettings, EnableNewSidebar, EnableStudioSidebar } =
    useFeatureFlags();
  const { pathname } = React.useContext(RouteContext);
  const theme = useTheme();
  // The main sidebar is only resizable from md up; below that both are the same mobile drawer.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { visibility } = useCompanySwitcherVisibility();

  if (
    !EnableNewSettings ||
    !EnableNewSidebar ||
    isMobile ||
    !pathname.includes('settings')
  ) {
    return null;
  }

  const percent = readMainSidebarPercent(visibility === 'desktop');
  const minPx = getSidebarMinPx(EnableStudioSidebar);
  // A collapsed main sidebar saves 0; Settings still needs its nav, so it opens at the default.
  if (!percent) return getSidebarDefaultPx(EnableStudioSidebar);

  const px = Math.round((percent / 100) * document.documentElement.clientWidth);
  return Math.min(Math.max(px, minPx), SIDEBAR_MAX_PX);
};
